import type { ReactNode } from "react";
import { cn } from "../lib/cn.ts";
import type { HTMLProps } from "../lib/props.ts";

export interface SpinnerProps extends HTMLProps<"span"> {
  /** Announced to screen readers; pass "" for a purely decorative spinner. */
  label?: string;
}

export function Spinner({
  label = "Loading",
  class: klass,
  className,
  ...props
}: SpinnerProps) {
  return (
    <span
      role={label ? "status" : undefined}
      aria-label={label || undefined}
      className={cn("inline-flex", klass, className)}
      {...props}
    >
      <span
        aria-hidden="true"
        className={
          "h-4 w-4 animate-spin rounded-full border-2 border-current " +
          "border-t-transparent"
        }
      />
    </span>
  );
}

/** Grey placeholder block for loading states. Give it width/height classes. */
export function Skeleton({
  class: klass,
  className,
  ...props
}: HTMLProps<"div">) {
  return (
    <div
      aria-hidden="true"
      className={cn("rounded-md bg-muted", klass, className)}
      {...props}
    />
  );
}

export interface EmptyStateProps extends HTMLProps<"div"> {
  title: string;
  description?: string;
  /** Optional icon element, e.g. `<Users className="h-6 w-6" />`. */
  icon?: ReactNode;
  /** Optional call to action rendered under the description. */
  action?: ReactNode;
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  class: klass,
  className,
  ...props
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-lg " +
          "border border-dashed border-border px-6 py-12 text-center",
        klass,
        className,
      )}
      {...props}
    >
      {icon && (
        <div className="grid h-11 w-11 place-items-center rounded-full bg-accent text-primary">
          {icon}
        </div>
      )}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && (
        <p className="max-w-sm text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
